// ---------------------------------------------------------------------------
// Banii — UN SINGUR formator în toată interfața
// ---------------------------------------------------------------------------
// Un `const lei = (v) => ...` per pagină ar bate moneda în cuie în ~150 de
// locuri. Cu EUR ca monedă
// de bază (prețurile devizului de referință sunt în EUR), tot ce afișează bani
// trece pe aici. Așa comutatorul EUR/lei e o singură linie, nu o campanie.
//
// Valorile care vin de la server sunt MEREU în moneda de bază (EUR). Cursul e
// informativ, pentru cine vrea cifra în lei; el vine tot de la server
// (`config.curs_lei_eur`), ca să nu existe două cursuri diferite.

import { numar, t } from "../i18n";

export type Moneda = "EUR" | "RON";

export const CURS_IMPLICIT = 5.3495;

let monedaAfisata: Moneda = "EUR";
let curs = CURS_IMPLICIT;

export function setMonedaAfisata(m: Moneda) {
  monedaAfisata = m;
}

export function getMonedaAfisata(): Moneda {
  return monedaAfisata;
}

export function setCurs(v: number | null | undefined) {
  if (v && v > 0) curs = v;
}

// «lei» e numele monedei, deci trece prin catalog: în altă limbă se scrie
// altfel, iar «€» e un simbol și rămâne cum e.
function simbol(m: Moneda): string {
  return m === "RON" ? t("struct.bani.lei") : "€";
}

/** Simbolul monedei afișate, pentru unitățile scrise de mână («€/m²»). */
export function simbolMoneda(): string {
  return simbol(monedaAfisata);
}

/** Suma în milioane, pentru plăcile de sus: «35,69 M€» / «177,72 mil. lei». */
export function baniMilioane(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  const val = (monedaAfisata === "RON" ? v * curs : v) / 1e6;
  const txt = numar(val, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return monedaAfisata === "RON" ? t("struct.bani.milioane_lei", { v: txt }) : `${txt} M€`;
}

/** Suma, în moneda afișată. `v` e în EUR (moneda de bază a serverului). */
export function bani(
  v: number | null | undefined,
  opt: { zecimale?: number; simbol?: boolean } = {},
): string {
  if (v == null || Number.isNaN(v)) return "—";
  const zec = opt.zecimale ?? 0;
  const val = monedaAfisata === "RON" ? v * curs : v;
  const txt = numar(val, {
    minimumFractionDigits: zec,
    maximumFractionDigits: zec,
  });
  return opt.simbol === false ? txt : `${txt} ${simbol(monedaAfisata)}`;
}

/** Preț unitar: MEREU două zecimale. Într-un deviz, 17,65 €/m² rotunjit la
 *  «18 €» pierde exact cifra pe care o compari cu oferta antreprenorului. */
export function pretUnitar(
  v: number | null | undefined,
  um?: string | null,
): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${bani(v, { zecimale: 2 })}${um ? `/${um}` : ""}`;
}

/** Sumă pe metru pătrat desfășurat. */
export function baniPeM2(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${bani(v, { zecimale: Math.abs(v) < 10 ? 1 : 0 })}/m²`;
}

